import { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import './WorkoutBuilderDemo.css';
import WorkoutSessionCard from '../components/WorkoutSessionCard';
import { type WorkoutDay, blankSession, applyModalityChange } from '../lib/workoutOptions';

// ─── Types ────────────────────────────────────────────────────────────────────

interface SurveyAnswers {
  workout_types: string[];
  best_days: string[];
  preferred_time: string;
  sessions_per_week: string;
  duration: string;
  intensity: string;
  other_notes: string;
}

type Mode = 'build' | 'survey';

// ─── Constants ────────────────────────────────────────────────────────────────

const SURVEY_WORKOUT_TYPES = [
  { label: 'Running',        value: 'running'     },
  { label: 'Walking',        value: 'walking'     },
  { label: 'Strength',       value: 'strength'    },
  { label: 'HIIT',           value: 'hiit'        },
  { label: 'Yoga',           value: 'yoga'        },
  { label: 'Mobility',       value: 'mobility'    },
  { label: 'Core & Stretch', value: 'core'        },
  { label: 'Group Classes',  value: 'group_class' },
  { label: 'Sports',         value: 'sport'       },
  { label: 'Cycling/Cardio', value: 'cardio'      },
];

const SURVEY_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const SURVEY_TIMES = [
  { label: 'Early morning', value: 'early_morning', desc: '5–8 AM'    },
  { label: 'Morning',       value: 'morning',       desc: '8–10 AM'   },
  { label: 'Midday',        value: 'midday',        desc: '10 AM–1 PM'},
  { label: 'Afternoon',     value: 'afternoon',     desc: '1–5 PM'    },
  { label: 'Evening',       value: 'evening',       desc: '5–9 PM'    },
  { label: 'Flexible',      value: 'flexible',      desc: 'Any time'  },
];

const SURVEY_SPW = [
  { label: '2–3 days', value: '2-3' },
  { label: '3–4 days', value: '3-4' },
  { label: '4–5 days', value: '4-5' },
  { label: '5+ days',  value: '5+'  },
];

const SURVEY_DURATION = [
  { label: '< 30 min',  value: 'under_30' },
  { label: '30–45 min', value: '30_45'    },
  { label: '45–60 min', value: '45_60'    },
  { label: '60+ min',   value: 'over_60'  },
];

const SURVEY_INTENSITY = [
  { label: 'Light',       value: 'light',       desc: 'Active recovery, low sweat'   },
  { label: 'Moderate',    value: 'moderate',    desc: 'Challenging but sustainable'  },
  { label: 'Challenging', value: 'challenging', desc: 'Push yourself most sessions'  },
  { label: 'Max effort',  value: 'max',         desc: 'Go hard every time'           },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const blankSurvey = (): SurveyAnswers => ({
  workout_types: [], best_days: [], preferred_time: '',
  sessions_per_week: '', duration: '', intensity: '', other_notes: '',
});

// ─── Component ────────────────────────────────────────────────────────────────

export default function WorkoutBuilder() {
  const [searchParams] = useSearchParams();
  const navigate       = useNavigate();
  const userId         = searchParams.get('u') ?? '';

  const [mode, setMode]         = useState<Mode>('build');
  const [sessions, setSessions] = useState<WorkoutDay[]>([]);
  const [survey, setSurvey]     = useState<SurveyAnswers>(blankSurvey());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState<string | null>(null);

  const apiBase = import.meta.env.VITE_API_URL || '';

  // ── Builder helpers ──
  const addSession       = () => setSessions(prev => [...prev, blankSession()]);
  const removeSession    = (i: number) => setSessions(prev => prev.filter((_, idx) => idx !== i));
  const duplicateSession = (i: number) =>
    setSessions(prev => [...prev.slice(0, i + 1), { ...prev[i], day: '' }, ...prev.slice(i + 1)]);

  const setField = <K extends keyof WorkoutDay>(i: number, field: K, value: WorkoutDay[K]) =>
    setSessions(prev => prev.map((s, idx) => idx === i ? { ...s, [field]: value } : s));

  const setModality = (i: number, mod: string) =>
    setSessions(prev => prev.map((s, idx) => idx === i ? applyModalityChange(s, mod) : s));

  const togglePill = <K extends 'strength_focus' | 'equipment'>(i: number, field: K, val: string) =>
    setField(i, field, sessions[i][field] === val ? '' : val);

  // ── Survey helpers ──
  const toggleSurveyMulti = (field: 'workout_types' | 'best_days', val: string) =>
    setSurvey(prev => {
      const cur = prev[field];
      return { ...prev, [field]: cur.includes(val) ? cur.filter(v => v !== val) : [...cur, val] };
    });

  const setSurveyField = (field: keyof SurveyAnswers, val: string) =>
    setSurvey(prev => ({ ...prev, [field]: val }));

  const surveyValid = survey.workout_types.length > 0 && survey.best_days.length > 0;
  const buildValid  = sessions.length > 0 && sessions.every(s => s.modality && s.day);

  // ── Submit ──
  const handleSubmit = async () => {
    if (!userId) {
      setError('Missing user token. Please use the link sent to you via SMS.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const body = mode === 'build'
        ? { user_id: userId, sessions }
        : { user_id: userId, ...survey };

      const res  = await fetch(`${apiBase}/api/plans/create`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Plan creation failed');
      }
      navigate(`/plan/${data.nanoid}`);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
      setSubmitting(false);
    }
  };

  // ── Invalid link guard ──
  if (!userId) {
    return (
      <div className="wb-page">
        <div className="wb-root">
          <div className="wb-container">
            <div className="wb-header">
              <h1 className="wb-title">Plan Builder</h1>
              <p className="wb-subtitle" style={{ color: '#e55' }}>
                Invalid or missing link. Please use the link sent to you via SMS.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="wb-page">
      <div className="wb-root">
        <div className="wb-container">

          <div className="wb-header">
            <h1 className="wb-title">Build Your Routine</h1>
            <p className="wb-subtitle">Choose how you'd like to set up your workout plan.</p>
          </div>

          {/* Mode switcher */}
          <div className="wb-mode-row">
            <button
              className={`wb-mode-btn${mode === 'build' ? ' wb-mode-btn--active' : ''}`}
              onClick={() => setMode('build')}
            >
              <span className="wb-mode-label">Build it myself</span>
              <span className="wb-mode-desc">Choose your days, times, and workout types</span>
            </button>
            <button
              className={`wb-mode-btn${mode === 'survey' ? ' wb-mode-btn--active' : ''}`}
              onClick={() => setMode('survey')}
            >
              <span className="wb-mode-label">Quick survey</span>
              <span className="wb-mode-desc">Answer a few questions, AI builds your plan</span>
            </button>
          </div>

          {/* ── Build mode ── */}
          {mode === 'build' && (
            <>
              <div className="wb-session-list">
                {sessions.map((session, i) => (
                  <WorkoutSessionCard
                    key={i}
                    session={session}
                    onField={(field, value) => setField(i, field, value)}
                    onModality={mod => setModality(i, mod)}
                    onTogglePill={(field, val) => togglePill(i, field, val)}
                    onRemove={() => removeSession(i)}
                    onDuplicate={() => duplicateSession(i)}
                  />
                ))}
              </div>

              <button className="wb-add-btn" onClick={addSession}>+ Add Workout Session</button>

              {error && <p className="ep-submit-error" style={{ margin: '12px 0' }}>{error}</p>}

              <div className="wb-nav">
                <button className="wb-btn-primary" disabled={!buildValid || submitting} onClick={handleSubmit}>
                  {submitting ? 'Building your plan…' : 'Build My Routine'}
                </button>
                {submitting && (
                  <p className="wb-survey-hint" style={{ textAlign: 'center', marginTop: 8 }}>
                    This usually takes 20–30 seconds — we're creating your full workout schedule.
                  </p>
                )}
              </div>
            </>
          )}

          {/* ── Survey mode ── */}
          {mode === 'survey' && (
            <>
              <div className="wb-survey-card">

                <div className="wb-survey-section">
                  <p className="wb-survey-q">What types of workouts do you enjoy?</p>
                  <p className="wb-survey-hint">Pick as many as you like</p>
                  <div className="wb-pills">
                    {SURVEY_WORKOUT_TYPES.map(opt => (
                      <button key={opt.value} type="button"
                        className={`wb-pill${survey.workout_types.includes(opt.value) ? ' wb-pill--active' : ''}`}
                        onClick={() => toggleSurveyMulti('workout_types', opt.value)}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="wb-divider" />

                <div className="wb-survey-section">
                  <p className="wb-survey-q">Which days usually work best?</p>
                  <p className="wb-survey-hint">Select all that apply</p>
                  <div className="wb-pills">
                    {SURVEY_DAYS.map(d => (
                      <button key={d} type="button"
                        className={`wb-pill${survey.best_days.includes(d) ? ' wb-pill--active' : ''}`}
                        onClick={() => toggleSurveyMulti('best_days', d)}>
                        {d}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="wb-survey-section">
                  <p className="wb-survey-q">What time of day works best?</p>
                  <div className="wb-pills">
                    {SURVEY_TIMES.map(opt => (
                      <button key={opt.value} type="button"
                        className={`wb-pill${survey.preferred_time === opt.value ? ' wb-pill--active' : ''}`}
                        onClick={() => setSurveyField('preferred_time', survey.preferred_time === opt.value ? '' : opt.value)}>
                        {opt.label} <span style={{ opacity: 0.6, fontSize: 11 }}>· {opt.desc}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="wb-divider" />

                <div className="wb-survey-section">
                  <p className="wb-survey-q">How many days per week?</p>
                  <div className="wb-pills">
                    {SURVEY_SPW.map(opt => (
                      <button key={opt.value} type="button"
                        className={`wb-pill${survey.sessions_per_week === opt.value ? ' wb-pill--active' : ''}`}
                        onClick={() => setSurveyField('sessions_per_week', survey.sessions_per_week === opt.value ? '' : opt.value)}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="wb-survey-section">
                  <p className="wb-survey-q">How long should each session be?</p>
                  <div className="wb-pills">
                    {SURVEY_DURATION.map(opt => (
                      <button key={opt.value} type="button"
                        className={`wb-pill${survey.duration === opt.value ? ' wb-pill--active' : ''}`}
                        onClick={() => setSurveyField('duration', survey.duration === opt.value ? '' : opt.value)}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="wb-survey-section">
                  <p className="wb-survey-q">How intense should workouts be?</p>
                  <div className="wb-pills">
                    {SURVEY_INTENSITY.map(opt => (
                      <button key={opt.value} type="button"
                        className={`wb-pill${survey.intensity === opt.value ? ' wb-pill--active' : ''}`}
                        onClick={() => setSurveyField('intensity', survey.intensity === opt.value ? '' : opt.value)}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="wb-divider" />

                <div className="wb-survey-section">
                  <p className="wb-survey-q">Anything else we should know?</p>
                  <p className="wb-survey-hint">Injuries, equipment access, specific goals...</p>
                  <textarea className="wb-textarea" rows={3} value={survey.other_notes}
                    onChange={e => setSurveyField('other_notes', e.target.value)}
                    placeholder="e.g. I have a bad knee. I only have dumbbells at home. I'm training for a 5K."
                    maxLength={400} />
                </div>

              </div>

              {error && <p className="ep-submit-error" style={{ margin: '12px 0' }}>{error}</p>}

              <div className="wb-nav">
                <button className="wb-btn-primary" disabled={!surveyValid || submitting} onClick={handleSubmit}>
                  {submitting ? 'Building your plan…' : 'Generate My Plan'}
                </button>
                {submitting && (
                  <p className="wb-survey-hint" style={{ textAlign: 'center', marginTop: 8 }}>
                    This usually takes 20–30 seconds — we're creating your full workout schedule.
                  </p>
                )}
              </div>
            </>
          )}

        </div>
      </div>
    </div>
  );
}
